/* eslint-disable no-nested-ternary */
import { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Checkbox, message } from 'antd';
import { any, object, string } from 'prop-types';
import { useRouter } from 'next/router';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import dynamic from 'next/dynamic';

import { PhoneOutlineSVG } from 'components/iconsSVG/phoneOutline';
import { WhatsappOutlineSVG } from 'components/iconsSVG/whatsappOutline';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import FormInput from 'components/formInput';
import Layout from 'components/layout';
import HeadSEO from 'components/base/headSEO';
import ProtectionDataModal from 'components/protectionDataModal';
import { ExtraInput, ExtraInputs } from 'components/extras';
import FileUploader from 'components/fileUploader';
import { getContactFormInfo, createContact } from 'actions/contact';
import { defaultLocale } from 'utils/translations';
import { getMarket, getLang, COOKIES, getCookie } from 'utils/helpers';
import { initCMS } from 'utils/cms';
import { contactColumns } from 'utils/conditionalFields';
import { useI18n } from 'context/i18n';

import { dataLayerHandleEvent } from 'utils/dataLayers';
import { useLocale, useValidate } from 'hooks';
import { isBlank } from 'utils/StringUtils';
import { getStore } from 'actions/shops';
import { Button as LabButton } from 'components/ui/button';
import { PrivacyPolicyText } from 'components/legalTexts';
import { getDomainValue } from 'utils/domain';

import contactStyles from 'styles/Contact.module.css';

const MyOrderModal = dynamic(() => import('components/myOrderModal').then((mod) => mod.MyOrderModal));
const CamperlabMyOrders = dynamic(() => import('components/camperlab/camperlab-order-drawer').then((mod) => mod.CamperlabMyOrders));

const { Option } = Select;

const SHOE_REPAIR_CODE = '23';
const ONLINE_STORE = '59';
const OFFLINE_STORE = '60';
const COMPLAINT_CODE = '7';
const COMPLAINT_CODE_MAX_COUNT_FILES = 10;
const COMPLAINT_CODE_MIN_COUNT_FILES = 1;
const MAX_COUNT_FILES = 10;
const MIN_COUNT_FILES = 2;
const KBOIX_FEEDBACK_CODE = '24';

const ContactFormCaptcha = ({
  info,
  defaultSelectedReason,
  defaultName,
  defaultSurnames,
  defaultEmail,
  defaultPhone,
  defaultAddress,
  defaultPostalCode,
  defaultCity,
  defaultExtraData,
  defaultSubReason,
}) => {
  const { t, locale, profileData } = useI18n();
  const { country } = useLocale();
  const validate = useValidate();
  const [ContactForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const [selectedReason, setSelectedReason] = useState();
  const [selectedReasonSubreasons, setSelectedReasonSubreasons] = useState([]);
  const [selectedSubreason, setSelectedSubreason] = useState(defaultSubReason);
  const [dataModalVisible, setDataModalVisible] = useState(false);
  const [isShopRequired, setIsShopRequired] = useState(false);
  const [filesToUploadShoes, setFilesShoes] = useState([]);
  const [filesToUploadTicket, setFilesTicket] = useState([]);
  const [filesErrorsShoes, setFilesErrorsShoes] = useState([]);
  const [filesErrorsTicket, setFilesErrorsTicket] = useState([]);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { monthYears } = info;
  const storeID = getCookie(COOKIES.TPV_STORE_ID);

  const typeOfStore = [undefined, null].includes(storeID) ? ONLINE_STORE : OFFLINE_STORE;
  const privacyURL = profileData?.urlPrivacyPolicy;

  const preffix = info && info.phonePrefixes ? info.phonePrefixes.filter((item) => item.id === getMarket(locale))[0].name : '';

  const columnsForExtras = contactColumns(info);

  // const validateImages = (fieldName, validation) => {
  //   const files = ContactForm.getFieldValue(fieldName);

  //   if (files === undefined) { // nunca ha sido rellenado
  //     return false;
  //   }

  //   const { fileList } = files;

  //   if (fileList) {
  //     return validation(fileList);
  //   }

  //   return false;
  // };

  const onFinishContactForm = async (values) => {
    let filesErrorShoes = false;
    if (selectedReason === SHOE_REPAIR_CODE) {
      if (filesToUploadShoes.length > MAX_COUNT_FILES) {
        filesErrorShoes = true;
        setFilesErrorsShoes([{ type: 'maxCount' }]);
      }
      if (filesToUploadShoes.length < MIN_COUNT_FILES) {
        filesErrorShoes = true;
        setFilesErrorsShoes([{ type: 'minCount' }]);
      }
      if (filesErrorsShoes.length > 0) {
        filesErrorShoes = true;
      }
    }
    if (selectedReason === COMPLAINT_CODE) {
      if (filesToUploadTicket.length > COMPLAINT_CODE_MAX_COUNT_FILES) {
        setFilesErrorsTicket([{ type: 'maxCount' }]);
        filesErrorShoes = true;
      }
      if (filesToUploadTicket.length < COMPLAINT_CODE_MIN_COUNT_FILES) {
        setFilesErrorsTicket([{ type: 'minCount' }]);
        filesErrorShoes = true;
      }
      if (!filesErrorShoes) {
        setFilesErrorsTicket([]);
      }
    }
    if (!filesErrorShoes) {
      setLoading(true);
      dataLayerHandleEvent({
        event: 'generateInbentaTicket',
      });
      try {
        let filesToUpload = [];
        if (filesToUploadTicket.length) {
          filesToUpload = [...filesToUpload, ...filesToUploadTicket.map((file) => file.src.file)];
        }
        if (filesToUploadShoes.length) {
          filesToUpload = [...filesToUpload, ...filesToUploadShoes.map((file) => file.src.file)];
        }
        if (filesToUpload.length) {
          values.files = filesToUpload;
        }
        const token = await executeRecaptcha('contact_page');

        if (selectedReason === SHOE_REPAIR_CODE) {
          // como es un input read only tendrá el name pero no el id
          const realSubReason = selectedReasonSubreasons.filter((item) => item.name === values.subreason)[0];

          if (realSubReason !== undefined) {
            values.subreason = realSubReason.id;
          }

          if (selectedSubreason === OFFLINE_STORE) {
            values.storeID = storeID;
          }
        }
        const response = await createContact({ lang: locale, token, values });
        if (response.status === 'OK') {
          router.push('/at_cliente_message');
        } else {
          message.error(t('generico', 'error.api.modal.titulo'));
          setLoading(false);
        }
      } catch (rsError) {
        console.error(rsError);
        message.error(t('generico', 'error.api.modal.titulo'));
        setLoading(false);
      }
    }
  };

  const onFinishFailedContactForm = (errorInfo) => {
    console.error('Failed:', errorInfo);
    let filesShoesError = false;
    if (selectedReason === SHOE_REPAIR_CODE) {
      if (filesToUploadShoes.length > MAX_COUNT_FILES) {
        setFilesErrorsShoes([{ type: 'maxCount' }]);
        filesShoesError = true;
      }
      if (filesToUploadShoes.length < MIN_COUNT_FILES) {
        setFilesErrorsShoes([{ type: 'minCount' }]);
        filesShoesError = true;
      }
      if (!filesShoesError) {
        setFilesErrorsShoes([]);
      }
    }
    if (selectedReason === COMPLAINT_CODE) {
      if (filesToUploadTicket.length > COMPLAINT_CODE_MAX_COUNT_FILES) {
        setFilesErrorsTicket([{ type: 'maxCount' }]);
        filesShoesError = true;
      }
      if (filesToUploadTicket.length < COMPLAINT_CODE_MIN_COUNT_FILES) {
        setFilesErrorsTicket([{ type: 'minCount' }]);
        filesShoesError = true;
      }
      if (!filesShoesError) {
        setFilesErrorsTicket([]);
      }
    }
  };

  const onChangeReason = (value) => {
    ContactForm.resetFields();
    ContactForm.setFieldsValue({ reason: value });
    setSelectedReason(value);
    setSelectedSubreason(undefined);
    const currentReason = info.reasons.filter((reason) => reason.id === value).shift();
    if (currentReason) {
      if (value === SHOE_REPAIR_CODE) {
        const filteredSubReason =
          defaultSubReason ?
            currentReason.subreasons.find((item) => item.id === defaultSubReason)
          : currentReason.subreasons.find((item) => item.id === typeOfStore);
        ContactForm.setFieldsValue({ subreason: filteredSubReason?.name });
        setSelectedSubreason(filteredSubReason?.id);
      }
      setSelectedReasonSubreasons(currentReason.subreasons);
    } else {
      setSelectedReasonSubreasons([]);
    }
  };

  useEffect(() => {
    if (defaultSelectedReason && info) {
      if (info.reasons && info.reasons.filter((reason) => reason.id === defaultSelectedReason).length) {
        onChangeReason(defaultSelectedReason);
      }
    }
  }, []);

  const extraInputsMap = {
    1: <ExtraInput items={columnsForExtras[1]} />,
    2: null,
    3: null,
    4: <ExtraInput items={columnsForExtras[4]} />,
    5: <ExtraInput items={columnsForExtras[5]} />,
    6: <ExtraInput items={columnsForExtras[6]} />,
    7: (
      <ExtraInputs
        isShopRequired={isShopRequired}
        setIsShopRequired={setIsShopRequired}
        monthYears={monthYears}
        ContactForm={ContactForm}
        filesToUploadTicket={filesToUploadTicket}
        filesErrorsTicket={filesErrorsTicket}
        setFilesErrorsTicket={setFilesErrorsTicket}
        setFilesTicket={setFilesTicket}
      />
    ),
    8: <ExtraInput items={columnsForExtras[8]} />,
    23: <ExtraInput items={columnsForExtras[23]} />,
  };

  let extraDefault = {};

  if (defaultExtraData) {
    extraDefault = { ...defaultExtraData };
  }

  const currentSubReason = selectedReasonSubreasons?.find((item) => item.id === selectedSubreason);
  const returnFaqShortAnswer = currentSubReason?.instantFAQ;

  const onClickFaq = (e) => {
    const el = e.target;
    if (el && el.tagName === 'A' && el.dataset && el.dataset.action === 'levantar-pop-up') {
      window.showOrderPopUp();
    }
  };

  return (
    <>
      <Form
        form={ContactForm}
        layout="vertical"
        initialValues={{
          countryId: getMarket(locale),
          city: defaultCity || '',
          name: defaultName || '',
          surnames: defaultSurnames || '',
          email: defaultEmail || '',
          address: defaultAddress || '',
          postalCode: defaultPostalCode || '',
          telephone: defaultPhone || '',
          preffix,
          ...extraDefault,
        }}
        name="contact"
        onFinish={onFinishContactForm}
        onFinishFailed={onFinishFailedContactForm}
        requiredMark="optional"
        scrollToFirstError
        className={`${contactStyles.contactForm} customForm`}
      >
        <FormInput floatLabel={t('contacto', 'selecciona.motivo.contacto')} name="reason" rules={validate('requiredOption')} required hasFeedback>
          <Select
            suffixIcon={<ChevronSVG />}
            tabIndex="1"
            placeholder=""
            defaultValue={defaultSelectedReason}
            virtual={false}
            dropdownClassName={contactStyles.dropdown}
            onSelect={(value) => {
              onChangeReason(value);
            }}
          >
            {info && info.reasons && (
              <>
                {info.reasons.map((item, index) => (
                  <Option key={`title-${index}`} value={item.id}>
                    {item.name}
                  </Option>
                ))}
              </>
            )}
          </Select>
        </FormInput>
        {selectedReasonSubreasons && selectedReasonSubreasons.length > 0 && (
          <FormInput
            floatLabel={
              [SHOE_REPAIR_CODE].includes(selectedReason) ?
                t('contacto', 'selecciona.donde.compraste.contacto')
              : t('contacto', 'selecciona.sub.motivo.contacto', 'Select subreason')
            }
            name="subreason"
            rules={validate('requiredOption')}
            required
            hasFeedback
          >
            <Select
              suffixIcon={<ChevronSVG />}
              tabIndex="1"
              placeholder=""
              virtual={false}
              dropdownClassName={contactStyles.dropdown}
              onSelect={(value) => {
                setSelectedSubreason(value);
              }}
            >
              {selectedReasonSubreasons.map((item, index) => (
                <Option key={`title-${index}`} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </FormInput>
        )}
        {returnFaqShortAnswer && (
          <div
            onClick={onClickFaq}
            className={`${contactStyles.repairMessage} ${contactStyles.faqMessage}`}
            dangerouslySetInnerHTML={{ __html: returnFaqShortAnswer }}
          />
        )}
        {currentSubReason?.additionalOptions?.length > 0 && (
          <FormInput floatLabel={t('contacto', 'type.of.repair')} name="additionalOption" rules={validate('requiredOption')} required hasFeedback>
            <Select suffixIcon={<ChevronSVG />} tabIndex="2" placeholder="" virtual={false} dropdownClassName={contactStyles.dropdown}>
              {currentSubReason.additionalOptions.map((item, index) => (
                <Option key={`additional-option-${index}`} value={index.toString()}>
                  {item}
                </Option>
              ))}
            </Select>
          </FormInput>
        )}
        <div className={contactStyles.messageInput}>
          <FormInput name="message" rules={validate('requiredField')} floatLabel={t('contacto', 'mensaje')} required>
            <Input.TextArea tabIndex="3" rows={6} placeholder="" />
          </FormInput>
        </div>
        <div className="grid gap-x-2 md:grid-cols-2">
          <FormInput name="name" rules={validate('name')} floatLabel={t('contacto', 'nombre')} required>
            <Input tabIndex="5" placeholder="" />
          </FormInput>
          <FormInput name="surnames" rules={validate('surname')} floatLabel={t('contacto', 'apellidos')} required>
            <Input tabIndex="6" placeholder="" />
          </FormInput>
          <FormInput name="email" rules={validate('email')} floatLabel={t('contacto', 'email')} required>
            <Input tabIndex="7" placeholder="" type="email" formNoValidate />
          </FormInput>
          <div className="grid grid-cols-2">
            <FormInput name="preffix" rules={[{ required: true }]} floatLabel={t('contacto', 'prefijo', 'Prefijo')} required>
              <Select suffixIcon={<ChevronSVG />} tabIndex="8" className={contactStyles.preffix}>
                {info && info.phonePrefixes && (
                  <>
                    {info.phonePrefixes.map((item, index) => (
                      <Option key={`preffix-${index}`} value={item.id}>
                        {item.name}
                      </Option>
                    ))}
                  </>
                )}
              </Select>
            </FormInput>
            <FormInput name="telephone" rules={validate('telephone')} floatLabel={t('contacto', 'telefono')} required>
              <Input tabIndex="9" placeholder="" type="tel" />
            </FormInput>
          </div>
          <FormInput name="countryId" rules={validate('country')} floatLabel={t('contacto', 'pais')} required>
            <Input tabIndex="10" placeholder="España" disabled />
          </FormInput>
          {['2', '3', COMPLAINT_CODE].includes(selectedReason) || (selectedReason === SHOE_REPAIR_CODE && selectedSubreason === ONLINE_STORE) ?
            <FormInput
              floatLabel={t('contacto', 'pedido', 'Num.de pedido')}
              name="orderId"
              rules={[SHOE_REPAIR_CODE, COMPLAINT_CODE].includes(selectedReason) ? validate('orderIdNotRequired') : validate('orderIdContact')}
              tooltip
              tooltipText={t(
                'contacto',
                'num.pedido.explicacion',
                '*** Añade tu número de pedido de camper.com (ej.: 400234232), o del comercio online donde adquiriste tus zapatos.',
              )}
            >
              <Input tabIndex={10} placeholder="" />
            </FormInput>
          : null}
          {extraInputsMap[selectedReason]}
        </div>
        {selectedReason === SHOE_REPAIR_CODE && selectedSubreason === OFFLINE_STORE ?
          <FileUploader
            form={ContactForm}
            name="ticketPicture"
            message={t(
              'contacto',
              'ticket.picture',
              '*** Para poder atender tu solicitud de manera sencilla y eficaz, te pedimos que nos adjuntes el ticket de compra.',
            )}
            files={filesToUploadTicket}
            setFiles={setFilesTicket}
            errors={filesErrorsTicket}
            setErrors={setFilesErrorsTicket}
          />
        : null}
        {selectedReason === SHOE_REPAIR_CODE ?
          <FileUploader
            minCount={MIN_COUNT_FILES}
            maxCount={MAX_COUNT_FILES}
            files={filesToUploadShoes}
            setFiles={setFilesShoes}
            errors={filesErrorsShoes}
            setErrors={setFilesErrorsShoes}
            name="shoePicture"
            sizeLimitMB={20}
            message={t(
              'contacto',
              'required.picture.selection',
              `*** De cara a gestionar tu solicitud, es necesario adjuntar fotos del artículo a reparar. Es condición imprescindible contar con una foto global del artículo así como una de la zona a ser reparada. Las solicitudes que no incluyan estas imágenes serán rechazadas. También nos sería útil contar con imágenes de la parte delantera y trasera.
              Las imágenes deben ocupar menos de 10MB y estar uno de los siguientes tipos de archivo: jpg, jpeg, gif, tif, tiff, bmp, png.`,
            )}
          />
        : null}
        <p onClick={() => setDataModalVisible(true)} className={contactStyles.infoLink}>
          {t('menu.derecha', 'informacion.proteccion.datos')}
        </p>
        <Form.Item name="agreement" valuePropName="checked" rules={validate('privacyPolicy')} className={contactStyles.privacyPolicyCheckbox}>
          <Checkbox tabIndex="27">
            {locale === 'ko_KR' || locale === 'ja_JP' ?
              <span>
                <a className={contactStyles.privacyPolicy} href={privacyURL} target="_blank" rel="noreferrer">
                  <span dangerouslySetInnerHTML={{ __html: t('contacto', 'condiciones.generales') }} />
                </a>
                {t('contacto', 'acepto.las', 'Acepto las')}
              </span>
            : country === 'US' ?
              <PrivacyPolicyText className={contactStyles.privacyPolicy} />
            : <span>
                {t('contacto', 'acepto.las', 'Acepto las')}{' '}
                <a className={contactStyles.privacyPolicy} href={privacyURL} target="_blank" rel="noreferrer">
                  <span dangerouslySetInnerHTML={{ __html: t('contacto', 'condiciones.generales') }} />
                </a>
                {selectedReason === SHOE_REPAIR_CODE ? ` ${t('compra.formulario', 'become.member')}` : null}
              </span>
            }
          </Checkbox>
        </Form.Item>
        <Form.Item>
          {getDomainValue({
            camper: (
              <Button className={contactStyles.submitContact} tabIndex="28" type="primary" htmlType="submit" loading={loading}>
                {t('contacto', 'enviar')}
              </Button>
            ),
            nnormal: 'camper',
            camperlab: (
              <div className="w-full items-center">
                <LabButton
                  tabIndex="28"
                  type="submit"
                  className="h-8 w-full px-2 text-sm font-bold uppercase lg:h-6 lg:w-1/2 lg:text-xs"
                  onPress={ContactForm.submit}
                  isPending={loading}
                >
                  {t('contacto', 'enviar')}
                </LabButton>
              </div>
            ),
          })}
        </Form.Item>
      </Form>
      <ProtectionDataModal visible={dataModalVisible} onCancel={() => setDataModalVisible(false)} origin="contact" />
    </>
  );
};

ContactFormCaptcha.propTypes = {
  locale: string,
  info: object,
  t: any,
  defaultSelectedReason: any,
  defaultName: string,
  defaultSurnames: string,
  defaultEmail: string,
  defaultPhone: string,
  defaultAddress: string,
  defaultPostalCode: string,
  defaultCity: string,
  defaultExtraData: any,
  defaultSubReason: any,
};

const globalEID = getDomainValue({
  camper: '82eb702a-cdc8-4401-92cf-b357fc6365ff',
  nnormal: '8DC7AD99-0A35-496A-9DDF-694A52C19769',
  camperlab: '82eb702a-cdc8-4401-92cf-b357fc6365ff',
});

function Contacto({
  defaultProps,
  info,
  defaultSelectedReason,
  defaultName,
  defaultSurnames,
  defaultEmail,
  defaultPhone,
  defaultAddress,
  defaultPostalCode,
  defaultCity,
  defaultExtraData,
  defaultSubReason,
}) {
  const { t, locale, profileData } = useI18n();
  const [showingMyOrderModal, setShowingMyOrderModal] = useState(false);

  const market = getMarket(locale);
  const language = getLang(locale);
  const hidePhoneNumbers = profileData?.config?.hide_telephone_numbers ?? false;

  useEffect(() => {
    if (window && profileData.global_e) {
      window.location.assign(`https://service.global-e.com/kb-tag/delivery-shipping?id=${globalEID}`);
    }
  }, []);

  const telephoneString = getLang(locale) === 'pl' ? t('contacto', 'telefono.pl', '(+48) 53 555 00 26') : profileData.phone;

  const ordersWidget = getDomainValue({
    camper: <MyOrderModal locale={locale} visible={showingMyOrderModal} onClose={() => setShowingMyOrderModal(false)} />,
    nnormal: <MyOrderModal locale={locale} visible={showingMyOrderModal} onClose={() => setShowingMyOrderModal(false)} />,
    camperlab: <CamperlabMyOrders visible={showingMyOrderModal} onClose={() => setShowingMyOrderModal(false)} />,
  });

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.RECAPTCHA_KEY}
      language={locale}
      useRecaptchaNet
      scriptProps={{
        async: false,
        defer: false,
        appendTo: 'body',
        nonce: undefined,
      }}
    >
      <Layout
        {...defaultProps}
        extratags={[<link key="canonical" rel="canonical" href={`${process.env.INTEGRA_DOMAIN}/${locale}/contacto`} />]}
        breadcrumbs={getDomainValue({ camper: true, nnormal: true, camperlab: false })}
      >
        <div>
          <HeadSEO description="Contacto" pageName="contact" />
          <main className="relative mx-auto flex w-full max-w-3xl flex-col items-center justify-center gap-6 px-4 lab:mt-40 md:flex-row md:items-start lg:max-w-4xl">
            <div className="flex-shrink-1 flex-grow">
              <div className={contactStyles.titleWrapper}>
                <h1 className={`${contactStyles.contactTitle} lab:mt-0`}>{t('contacto', 'contacto')}</h1>
                {getDomainValue({
                  camper: (
                    <Button className={contactStyles.ordersBtn} data-lang={language} block onClick={() => setShowingMyOrderModal(true)}>
                      {t('menu.superior', 'mis.pedidos')}
                    </Button>
                  ),
                  nnormal: 'camper',
                  camperlab: (
                    <LabButton data-lang={language} variant="outline" onPress={() => setShowingMyOrderModal(true)}>
                      {t('menu.superior', 'mis.pedidos')}
                    </LabButton>
                  ),
                })}
              </div>
              <ContactFormCaptcha
                info={info}
                defaultSelectedReason={defaultSelectedReason}
                defaultAddress={defaultAddress}
                defaultCity={defaultCity}
                defaultEmail={defaultEmail}
                defaultName={defaultName}
                defaultPhone={defaultPhone}
                defaultPostalCode={defaultPostalCode}
                defaultSurnames={defaultSurnames}
                defaultExtraData={defaultExtraData}
                defaultSubReason={defaultSubReason}
              />
            </div>
            {!hidePhoneNumbers ?
              <div className="flex-shrink-1 sticky top-16 mt-20 max-w-64">
                <h2>{t('menu.derecha', 'necesita.ayuda')}</h2>
                {profileData.phone && getDomainValue({ camper: true, camper: true, nnormal: getLang(locale) === 'pl' }) ?
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-1">
                      <PhoneOutlineSVG className="h-auto w-[30px]" />
                    </div>
                    <div className="text-xs">
                      <p className="mx-auto mb-0 text-sm">
                        {profileData.phone && (
                          <a href={`tel:${telephoneString}`}>
                            <u> {telephoneString}</u>
                          </a>
                        )}
                      </p>
                      <p
                        className="mb-0"
                        dangerouslySetInnerHTML={{
                          __html: t('menu.derecha', `derecha.contacta.horario.${market.toLowerCase()}`).replace(/(?:\r\n|\r|\n)/g, '<br />'),
                        }}
                      ></p>
                    </div>
                  </div>
                : null}
                {profileData.whatsapp && (
                  <div className="mt-4 flex items-center gap-4">
                    <div className="flex-shrink-1">
                      <WhatsappOutlineSVG className="w-[30px]" />
                    </div>
                    <div>
                      <p className="mx-auto mb-0 text-sm">
                        <a
                          href={`https://api.whatsapp.com/send?phone=${profileData.whatsapp.replace(/\s+/g, '').replace('+', '')}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <u> {profileData.whatsapp}</u>
                        </a>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            : null}
            {ordersWidget}
          </main>
        </div>
      </Layout>
    </GoogleReCaptchaProvider>
  );
}

Contacto.propTypes = {
  locale: string,
  info: object,
  footerProps: any,
  tnmProps: any,
  ribbon: any,
  defaultSelectedReason: any,
  defaultName: string,
  defaultSurnames: string,
  defaultEmail: string,
  defaultPhone: string,
  defaultAddress: string,
  defaultPostalCode: string,
  defaultCity: string,
  defaultExtraData: any,
  defaultSubReason: any,
};

Contacto.defaultProps = {
  locale: defaultLocale,
  info: null,
  footerProps: null,
  tnmProps: null,
};

Contacto.i18nPage = 'Contact';

const getStoreById = async (props) => {
  try {
    if (isBlank(props.id)) return null;
    const data = await getStore(props);
    return data;
  } catch (error) {
    return null;
  }
};

export async function getServerSideProps(context) {
  const { locale, query, req } = context;
  let { type, name, surnames, email, phone, address, postalCode, city } = query;

  const storeId = query.sales_point;
  let storeCookieId = null;
  const storeData = req.cookies[COOKIES.STORE_DATA];
  if (getDomainValue({ camper: true, camperlab: true, nnormal: false }) && storeData) {
    const storeDataParsed = JSON.parse(storeData);
    storeCookieId = storeDataParsed.centro;
  }

  const [{ defaultProps }, contactInfo, storeInfo] = await Promise.all([
    initCMS({ context, locale }),
    getContactFormInfo({ context, lang: locale }),
    getStoreById({ context, profile: locale, id: storeId ?? storeCookieId }),
  ]);

  const { reasons } = contactInfo;

  // type 24 es para el feedback de kboix. Si no tenemos esa id en el filtro quitar la opcion de las razones que nos llegan
  if (getDomainValue({ nnormal: true, camper: false, camperlab: false }) && type !== KBOIX_FEEDBACK_CODE) {
    contactInfo.reasons = reasons.filter((item) => item.id !== KBOIX_FEEDBACK_CODE);
  }

  const hasShoeRepair = reasons.some((item) => item.id === SHOE_REPAIR_CODE);

  let defaultExtraData = null;
  let defaultSubReason = null;

  if (hasShoeRepair) {
    const hasRepairInStore = reasons.some((item) => item.id === SHOE_REPAIR_CODE && item.subreasons.some((subreason) => subreason.id === OFFLINE_STORE));
    const shoeRepairCookie = req.cookies[COOKIES.SHOE_REPAIR_DATA];
    // rellenar los datos del formulario si tenemos datos de la tienda y es un formulario de shoe repair

    if (hasRepairInStore && storeInfo && (storeId || storeCookieId)) {
      type = SHOE_REPAIR_CODE;
      defaultSubReason = OFFLINE_STORE;
      name = storeInfo.id && storeInfo.name ? `${storeInfo.id} - ${storeInfo.name}` : undefined;
      surnames = storeInfo.id && storeInfo.name ? `${storeInfo.id} - ${storeInfo.name}` : undefined;
      email = storeInfo.email;
      phone = storeInfo.telephone?.split(' ').pop();
      address = storeInfo.address1;
      postalCode = storeInfo.postalCode;
      city = storeInfo.cityName;
    } else if (shoeRepairCookie) {
      const shoeRepairData = JSON.parse(shoeRepairCookie);

      if (shoeRepairData) {
        type = SHOE_REPAIR_CODE;

        ({ name, email, address, city, postalCode } = shoeRepairData);

        const { surname, telephone, orderId, sku } = shoeRepairData;

        surnames = surname;
        phone = telephone;
        defaultExtraData = {
          orderId: orderId ?? null,
          sku: sku ?? null,
        };
        defaultSubReason = orderId?.startsWith('0') ? OFFLINE_STORE : ONLINE_STORE;
      }
    }
  }

  return {
    props: {
      defaultProps,
      info: contactInfo,
      defaultSelectedReason: type || null,
      defaultName: name || '',
      defaultSurnames: surnames || '',
      defaultEmail: email || '',
      defaultPhone: phone || '',
      defaultAddress: address || '',
      defaultPostalCode: postalCode || '',
      defaultCity: city || '',
      defaultExtraData,
      defaultSubReason,
    },
  };
}

export default Contacto;
